import React, { useMemo } from 'react';
import { TestResult, CHEMICALS, ChemicalProduct, WaterBodyType } from '../../../types';
import { ActionCard, ActionCardProps } from './ActionCard';
import { DosageCalculator } from './DosageCalculator';

interface Props {
  results: TestResult | null;
  poolVolume: number;
  waterBody?: WaterBodyType;
}

type ActionItem = ActionCardProps & {
  dosage?: { fn: string; changeNeeded: number; label: string };
};

const getProducts = (fn: string): ChemicalProduct[] =>
  CHEMICALS.filter((c) => c.functions.includes(fn));

export const RuleBasedActionPlanTab: React.FC<Props> = ({
  results,
  poolVolume,
  waterBody = 'pool',
}) => {
  const isHotTub = waterBody === 'hot_tub';

  // Ideal ranges differ between pool and hot tub
  const ranges = useMemo(
    () => ({
      fc:  isHotTub ? { low: 3, high: 5 }   : { low: 2, high: 6 },
      ta:  { low: 80, high: 120 },
      ph:  { low: 7.2, high: 7.6, target: 7.4 },
      ch:  isHotTub ? { low: 150, high: 250 } : { low: 200, high: 400 },
      cya: isHotTub ? { low: 0, high: 50 }   : { low: 30, high: 50 },
      br:  isHotTub ? { low: 3, high: 5 }    : { low: 2, high: 6 },
    }),
    [isHotTub],
  );

  const actions = useMemo((): ActionItem[] => {
    if (!results) return [];
    const r = results;
    const items: ActionItem[] = [];

    // ── Dangerously high FC ──────────────────────────────────────────────────
    if (r.fc != null) {
      if (r.fc > 10) {
        items.push({
          title: '🚨 Dangerously High FC!',
          currentValue: `${r.fc.toFixed(1)} ppm`,
          idealRange: `${ranges.fc.low}–${ranges.fc.high} ppm`,
          status: 'critical',
          priority: 0,
        });
      } else if (r.fc > ranges.fc.high) {
        // Elevated but not dangerous — stop chlorination and wait
        items.push({
          title: '⬆️ High Free Chlorine',
          currentValue: `${r.fc.toFixed(1)} ppm`,
          idealRange: `${ranges.fc.low}–${ranges.fc.high} ppm`,
          status: 'high',
          priority: 0,
        });
      }
    }

    // ── High CC — needs breakpoint chlorination ──────────────────────────────
    if (r.cc != null && r.cc >= 0.5) {
      const dose = r.cc * 10;
      items.push({
        title: '⚠️ High Combined Chlorine',
        currentValue: `${r.cc.toFixed(1)} ppm`,
        idealRange: '< 0.5 ppm',
        status: 'critical',
        priority: 1,
        dosage: {
          fn: 'ADD_FC',
          changeNeeded: dose,
          label: `Breakpoint chlorinate (add ${dose.toFixed(0)} ppm FC)`,
        },
      });
    }

    // ── Bromine (hot tub) ────────────────────────────────────────────────────
    if (isHotTub && r.bromine != null) {
      if (r.bromine < ranges.br.low) {
        items.push({
          title: '⬇️ Low Bromine',
          currentValue: `${r.bromine.toFixed(1)} ppm`,
          idealRange: `${ranges.br.low}–${ranges.br.high} ppm`,
          status: 'low',
          priority: 1,
          dosage: {
            fn: 'ADD_BROMINE',
            changeNeeded: ranges.br.low - r.bromine,
            label: 'Raise Bromine',
          },
        });
      } else if (r.bromine > ranges.br.high) {
        items.push({
          title: '⬆️ High Bromine',
          currentValue: `${r.bromine.toFixed(1)} ppm`,
          idealRange: `${ranges.br.low}–${ranges.br.high} ppm`,
          status: 'high',
          priority: 1,
        });
      }
    }

    // ── Total Alkalinity ─────────────────────────────────────────────────────
    if (r.ta != null) {
      if (r.ta < ranges.ta.low) {
        items.push({
          title: '⬇️ Low Total Alkalinity',
          currentValue: `${r.ta} ppm`,
          idealRange: `${ranges.ta.low}–${ranges.ta.high} ppm`,
          status: 'low',
          priority: 2,
          dosage: { fn: 'RAISE_TA', changeNeeded: ranges.ta.low - r.ta, label: 'Raise TA' },
        });
      } else if (r.ta > ranges.ta.high) {
        items.push({
          title: '⬆️ High Total Alkalinity',
          currentValue: `${r.ta} ppm`,
          idealRange: `${ranges.ta.low}–${ranges.ta.high} ppm`,
          status: 'high',
          priority: 2,
          dosage: { fn: 'LOWER_PH_TA', changeNeeded: r.ta - 100, label: 'Lower TA' },
        });
      }
    }

    // ── pH ───────────────────────────────────────────────────────────────────
    if (r.ph != null) {
      if (r.ph < ranges.ph.low) {
        const delta = parseFloat((ranges.ph.target - r.ph).toFixed(2));
        items.push({
          title: '⬇️ Low pH',
          currentValue: `${r.ph.toFixed(1)}`,
          idealRange: `${ranges.ph.low}–${ranges.ph.target + 0.2}`,
          status: 'low',
          priority: 3,
          dosage: { fn: 'RAISE_PH', changeNeeded: delta, label: `Raise pH by ${delta}` },
        });
      } else if (r.ph > 7.8) {
        const delta = parseFloat((r.ph - ranges.ph.target).toFixed(2));
        items.push({
          title: '⬆️ High pH',
          currentValue: `${r.ph.toFixed(1)}`,
          idealRange: `${ranges.ph.low}–${ranges.ph.target + 0.2}`,
          status: 'high',
          priority: 3,
          dosage: { fn: 'LOWER_PH_TA', changeNeeded: delta, label: `Lower pH by ${delta}` },
        });
      }
    }

    // ── Free Chlorine ────────────────────────────────────────────────────────
    if (r.fc != null && (!isHotTub || r.fc > 0)) {
      const targetFC = r.cya != null && r.cya > 0 ? Math.max(ranges.fc.low, r.cya * 0.075) : ranges.fc.low;
      if (r.fc < targetFC && r.fc <= 10) {
        items.push({
          title: '⬇️ Low Free Chlorine',
          currentValue: `${r.fc.toFixed(1)} ppm`,
          idealRange: `${targetFC.toFixed(1)}–${(targetFC + 2).toFixed(1)} ppm${r.cya != null && r.cya > 0 ? ' (CYA-adj.)' : ''}`,
          status: 'low',
          priority: 4,
          dosage: { fn: 'ADD_FC', changeNeeded: targetFC - r.fc, label: 'Raise FC' },
        });
      }
    }

    // ── Calcium Hardness ─────────────────────────────────────────────────────
    if (r.ch != null) {
      if (r.ch < ranges.ch.low) {
        items.push({
          title: '⬇️ Low Calcium Hardness',
          currentValue: `${r.ch} ppm`,
          idealRange: `${ranges.ch.low}–${ranges.ch.high} ppm`,
          status: 'low',
          priority: 5,
          dosage: { fn: 'RAISE_CH', changeNeeded: ranges.ch.low - r.ch, label: 'Raise CH' },
        });
      } else if (r.ch > ranges.ch.high) {
        items.push({
          title: '⬆️ High Calcium Hardness',
          currentValue: `${r.ch} ppm`,
          idealRange: `${ranges.ch.low}–${ranges.ch.high} ppm`,
          status: 'high',
          priority: 5,
        });
      }
    }

    // ── CYA ──────────────────────────────────────────────────────────────────
    if (!isHotTub) {
      if (r.cya != null) {
        if (r.cya < ranges.cya.low) {
          items.push({
            title: '⬇️ Low CYA (Stabilizer)',
            currentValue: `${r.cya} ppm`,
            idealRange: `${ranges.cya.low}–${ranges.cya.high} ppm`,
            status: 'low',
            priority: 6,
            dosage: { fn: 'RAISE_CYA', changeNeeded: ranges.cya.low - r.cya, label: 'Raise CYA' },
          });
        } else if (r.cya > ranges.cya.high) {
          items.push({
            title: '⬆️ High CYA (Stabilizer)',
            currentValue: `${r.cya} ppm`,
            idealRange: `${ranges.cya.low}–${ranges.cya.high} ppm`,
            status: 'high',
            priority: 6,
          });
        }
      }
    } else if (r.cya != null && r.cya > 50) {
      // Hot tubs: CYA only relevant if using chlorine
      items.push({
        title: '⬆️ High CYA (Stabilizer)',
        currentValue: `${r.cya} ppm`,
        idealRange: '0–50 ppm',
        status: 'high',
        priority: 6,
      });
    }

    return items.sort((a, b) => a.priority - b.priority);
  }, [results, ranges, isHotTub]);

  if (!results) {
    return (
      <p className="text-center text-slate-400 py-8">Submit a test to see your action plan.</p>
    );
  }

  const bodyLabel = isHotTub ? '♨️ Hot Tub' : '🏊 Pool';

  return (
    <div>
      <p className="text-xs text-slate-500 mb-3 text-center">
        Ranges calibrated for: <span className="text-cyan-400 font-semibold">{bodyLabel}</span>
      </p>
      {actions.length === 0 && (
        <div className="bg-green-900/40 border border-green-500 rounded-lg p-4 mb-4 text-center">
          <p className="text-green-400 font-bold text-lg">✅ All Clear!</p>
          <p className="text-slate-300 text-sm">
            All parameters are within ideal ranges. No action needed.
          </p>
        </div>
      )}
      {actions.map((action, i) => (
        <ActionCard key={i} {...action}>
          {action.title.includes('Dangerously High') && (
            <p className="text-xs text-red-300 mt-1">
              ⛔ <strong>DO NOT SWIM/SOAK.</strong> Allow FC to dissipate naturally or use a
              chlorine neutralizer (sodium thiosulfate). Test before use.
            </p>
          )}
          {action.title === '⬆️ High Free Chlorine' && (
            <p className="text-xs text-orange-300 mt-1">
              🛑 <strong>Stop all chlorination</strong> (turn off feeders, SWG, or remove tablets).
              Keep the pump running and expose the water to sunlight — UV naturally degrades chlorine.
              Retest every 4–6 hours. Do not swim/soak until FC drops to {ranges.fc.low}–{ranges.fc.high} ppm.
            </p>
          )}
          {action.dosage && action.dosage.fn !== 'ADD_BROMINE' && (
            <DosageCalculator
              products={getProducts(action.dosage.fn)}
              poolVolume={poolVolume}
              changeNeeded={action.dosage.changeNeeded}
              label={action.dosage.label}
            />
          )}
          {action.dosage && action.dosage.fn === 'ADD_BROMINE' && (
            <DosageCalculator
              products={getProducts('ADD_BROMINE')}
              poolVolume={poolVolume}
              changeNeeded={action.dosage.changeNeeded}
              label={action.dosage.label}
            />
          )}
        </ActionCard>
      ))}
    </div>
  );
};

export default RuleBasedActionPlanTab;
